/**
 * The two accessibility preferences that change layout, supplied as explicit
 * state rather than sniffed by each consumer.
 *
 * `AccessibilityOverrides` in `@ff/domain` is the persisted shape; this context
 * carries the two fields the layout ladder actually branches on. The others
 * (Increase Contrast, Differentiate Without Color, Button Shapes) change paint,
 * not composition, and belong to the token layer.
 */

import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";
import type { SystemOverridablePreference, TextSizePreference } from "@ff/domain";
import { DEFAULT_ACCESSIBILITY_OVERRIDES } from "@ff/domain";

export interface LayoutPreferences {
  readonly textSize: TextSizePreference;
  readonly reduceMotion: SystemOverridablePreference;
}

/**
 * Defaults come from the domain record so "never configured" means the same
 * thing here as it does in storage; a second literal would be a second default
 * to keep in step.
 */
export const DEFAULT_LAYOUT_PREFERENCES: LayoutPreferences = {
  textSize: DEFAULT_ACCESSIBILITY_OVERRIDES.textSize,
  reduceMotion: DEFAULT_ACCESSIBILITY_OVERRIDES.reduceMotion,
};

const LayoutPreferencesContext = createContext<LayoutPreferences>(DEFAULT_LAYOUT_PREFERENCES);

export function useLayoutPreferences(): LayoutPreferences {
  return useContext(LayoutPreferencesContext);
}

export interface LayoutPreferencesProviderProps {
  readonly textSize?: TextSizePreference;
  readonly reduceMotion?: SystemOverridablePreference;
  readonly children?: ReactNode;
}

/**
 * Placed once, by the shell, from the persisted settings record. A feature that
 * needs the branch reads `useAccessibilityTextSize()`; it never reads the
 * preference and re-derives the rule, because the two would diverge the first
 * time the system heuristic changes.
 */
export function LayoutPreferencesProvider({
  textSize,
  reduceMotion,
  children,
}: LayoutPreferencesProviderProps): ReactNode {
  const resolvedTextSize = textSize ?? DEFAULT_LAYOUT_PREFERENCES.textSize;
  const resolvedReduceMotion = reduceMotion ?? DEFAULT_LAYOUT_PREFERENCES.reduceMotion;
  const value = useMemo<LayoutPreferences>(
    () => ({ textSize: resolvedTextSize, reduceMotion: resolvedReduceMotion }),
    [resolvedTextSize, resolvedReduceMotion],
  );
  return (
    <LayoutPreferencesContext.Provider value={value}>{children}</LayoutPreferencesContext.Provider>
  );
}
